import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import videoService from '../services/videoService';
import categoryService from '../services/categoryService';
import { SERVER_URL } from '../config/baseUrl';

const VideoEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    description: '',
    videoType: 'video',
    category: ''
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [autoGenerateThumbnail, setAutoGenerateThumbnail] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
  const [fetchingVideo, setFetchingVideo] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchVideo();
  }, [id]);

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getAllCategories();
      if (data && data.categories) {
        setCategories(data.categories);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchVideo = async () => {
    try {
      setFetchingVideo(true);
      const response = await videoService.getVideoById(id);
      const video = response.video;
      
      setFormData({
        url: video.url,
        title: video.title,
        description: video.description,
        videoType: video.videoType || 'video',
        category: video.category?._id || ''
      });

      // Set existing thumbnail preview
      if (video.thumbnail) {
        const thumbnailUrl = video.thumbnail.startsWith('http')
          ? video.thumbnail
          : `${SERVER_URL}${video.thumbnail}`;
        setThumbnailPreview(thumbnailUrl);
      }
    } catch (err) {
      setError('Failed to fetch video details');
      console.error(err);
    } finally {
      setFetchingVideo(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setThumbnailFile(file);
      setAutoGenerateThumbnail(false);
      setError('');

      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerateThumbnail = async () => {
    if (!formData.url) {
      setError('Please enter a YouTube URL first');
      return;
    }

    setGeneratingThumbnail(true);
    setError('');
    setSuccess('');

    try {
      console.log('🔄 Generating thumbnail for URL:', formData.url);
      const response = await videoService.generateThumbnail(formData.url);
      console.log('✅ Thumbnail response:', response);
      
      if (response.thumbnails) {
        const thumbnailUrl = response.thumbnails.maxres || response.thumbnails.hq || response.thumbnails.mq;
        
        if (thumbnailUrl) {
          setThumbnailPreview(thumbnailUrl);
          setAutoGenerateThumbnail(true);
          setThumbnailFile(null);
          setSuccess('Thumbnail generated successfully!');
          
          setTimeout(() => setSuccess(''), 3000);
        } else {
          console.error('❌ No thumbnail URLs in response:', response);
          setError('Could not generate thumbnail from this URL. Please check if it\'s a valid YouTube URL.');
        }
      } else {
        console.error('❌ Invalid response format:', response);
        setError('Could not generate thumbnail from this URL. Please check if it\'s a valid YouTube URL.');
      }
    } catch (err) {
      console.error('❌ Thumbnail generation error:', err);
      console.error('Error details:', {
        message: err.message,
        error: err.error,
        response: err.response,
        data: err.response?.data
      });
      
      // Extract error message from various possible formats
      let errorMessage = 'Failed to generate thumbnail. ';
      
      if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else if (err.message) {
        errorMessage += err.message;
      } else if (err.error) {
        errorMessage += err.error;
      } else {
        errorMessage += 'Please check if the URL is a valid YouTube video URL.';
      }
      
      setError(errorMessage);
    } finally {
      setGeneratingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        thumbnail: thumbnailFile,
        autoGenerateThumbnail: autoGenerateThumbnail
      };

      await videoService.updateVideo(id, submitData);
      setSuccess('Video updated successfully!');
      
      setTimeout(() => {
        navigate('/videos');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to update video. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingVideo) {
    return (
      <>
        <Sidebar />
        <div className="dashboard">
          <Navbar />
          <div className="container">
            <div className="loading">Loading video details...</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <div className="dashboard">
        <Navbar />
        <div className="container">
        
        <div className="auth-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h1>Edit Video</h1>
          <p>Update video details and thumbnail</p>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="videoType">Type *</label>
              <select
                id="videoType"
                name="videoType"
                value={formData.videoType}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="video">Video</option>
                <option value="shorts">Shorts</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">-- Select Category (Optional) --</option>
                {categories && categories.length > 0 ? (
                  categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading categories...</option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="url">YouTube Video URL *</label>
              <input
                type="url"
                id="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                required
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter video title"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Enter video description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="thumbnail">Thumbnail Image *</label>
              
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleGenerateThumbnail}
                  disabled={generatingThumbnail || !formData.url}
                  style={{
                    flex: '1',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: generatingThumbnail ? '#999' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: generatingThumbnail || !formData.url ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {generatingThumbnail ? '🔄 Generating...' : '🎬 Generate from YouTube'}
                </button>
                
                <label 
                  htmlFor="thumbnail" 
                  style={{
                    flex: '1',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  📁 Upload New Image
                </label>
              </div>

              <input
                type="file"
                id="thumbnail"
                name="thumbnail"
                onChange={handleFileChange}
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
              />
              
              <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                Generate thumbnail from YouTube URL or upload your own image (JPEG, PNG, GIF, WebP - Max 5MB)
              </small>
              
              {thumbnailPreview && (
                <div style={{ marginTop: '15px', position: 'relative' }}>
                  <img 
                    src={thumbnailPreview} 
                    alt="Thumbnail preview" 
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: '2px solid #667eea',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      display: 'block'
                    }}
                    onError={(e) => {
                      console.log('❌ Thumbnail failed to load, trying fallback...');
                      // If maxres fails, try hq quality
                      if (e.target.src.includes('maxresdefault')) {
                        e.target.src = e.target.src.replace('maxresdefault.jpg', 'hqdefault.jpg');
                      } 
                      // If hq fails, try mq quality
                      else if (e.target.src.includes('hqdefault')) {
                        e.target.src = e.target.src.replace('hqdefault.jpg', 'mqdefault.jpg');
                      }
                      // If all fail, show error message
                      else {
                        e.target.style.display = 'none';
                        const errorDiv = e.target.nextElementSibling;
                        if (errorDiv && errorDiv.classList.contains('thumbnail-error')) {
                          errorDiv.style.display = 'block';
                        }
                      }
                    }}
                  />
                  <div 
                    className="thumbnail-error"
                    style={{
                      display: 'none',
                      padding: '20px',
                      backgroundColor: '#fee',
                      border: '2px solid #f00',
                      borderRadius: '8px',
                      color: '#c00',
                      textAlign: 'center'
                    }}
                  >
                    ⚠️ Could not load thumbnail. Please try uploading an image instead.
                  </div>
                  {autoGenerateThumbnail && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      ✓ Auto-generated from YouTube
                    </div>
                  )}
                  {thumbnailFile && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: '#d1fae5',
                      color: '#065f46',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      ✓ New image uploaded
                    </div>
                  )}
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Video'}
            </button>

            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => navigate('/videos')}
              disabled={loading}
            >
              Cancel
            </button>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default VideoEdit;
